import React, { useState, useCallback, useEffect, useRef } from "react";
import { GoogleMap, useJsApiLoader, Marker } from "@react-google-maps/api";
import { Card, Button, Space, Typography, Spin, message, Alert } from "antd";
import { EnvironmentOutlined, AimOutlined } from "@ant-design/icons";

const { Text } = Typography;

const containerStyle = {
  width: "100%",
  height: "400px",
  borderRadius: "6px",
};

// Default center (Bangkok, Thailand)
const defaultCenter = { lat: 13.096092, lng: 100.964311 };
const libraries = ["places"];

/** ===== Auto-pan tuning (ปรับได้) ===== */
const EDGE_START = 80; // เข้าเขตเริ่มเลื่อน
const EDGE_RELEASE = 110; // ออกเขตถึงหยุดเลื่อน (ฮิสเทอรีซิส)
const MAX_STEP = 12; // px/เฟรม สูงสุด
const RATE = 0.8; // ความชันความเร็วตามระยะชิดขอบ
const SMOOTH_ALPHA = 0.25; // 0..1, ยิ่งมากยิ่งตอบสนองไว (แต่กระด้าง)

const MapPicker = ({ value, onChange, disabled = false }) => {
  const [map, setMap] = useState(null);
  const [marker, setMarker] = useState(
    value && value.lat && value.lng ? value : defaultCenter
  );
  const [isLocating, setIsLocating] = useState(false);
  const [mapReady, setMapReady] = useState(false);

  // drag state
  const isDraggingRef = useRef(false);
  const pointerPxRef = useRef({ x: null, y: null });

  // hysteresis state
  const nearEdgeXRef = useRef(false);
  const nearEdgeYRef = useRef(false);

  // smoothing state
  const velRef = useRef({ dx: 0, dy: 0 });
  const rafRef = useRef(null);

  const { isLoaded, loadError } = useJsApiLoader({
    id: "google-map-script",
    googleMapsApiKey: process.env.REACT_APP_GOOGLE_MAPS_API_KEY || "",
    libraries,
  });

  /** sync marker จาก props */
  useEffect(() => {
    if (
      value &&
      typeof value.lat === "number" &&
      typeof value.lng === "number"
    ) {
      setMarker(value);
    }
  }, [value]);

  useEffect(() => {
    return () => {
      stopAutoPan();
      detachPointerListeners();
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  /** ===== Auto-pan loop (ลื่น + hysteresis + smoothing) ===== */
  const autoPanTick = useCallback(() => {
    rafRef.current = null;
    if (!isDraggingRef.current || !map) return;

    const div = map.getDiv();
    const rect = div.getBoundingClientRect();
    const { x, y } = pointerPxRef.current || {};

    let targetDx = 0,
      targetDy = 0;

    // หากไม่ทราบตำแหน่งเมาส์ ให้รอต่อไป
    if (x == null || y == null) {
      rafRef.current = requestAnimationFrame(autoPanTick);
      return;
    }

    // ===== X-axis with hysteresis =====
    if (nearEdgeXRef.current) {
      // อยู่ในโหมดเลื่อนแล้ว จะหยุดก็ต่อเมื่อพ้นระยะ RELEASE
      if (x > EDGE_RELEASE && x < rect.width - EDGE_RELEASE) {
        nearEdgeXRef.current = false; // ออกขอบแล้ว
      }
    } else {
      // ยังไม่เข้าโหมดเลื่อน รอให้เข้า START
      if (x < EDGE_START || x > rect.width - EDGE_START) {
        nearEdgeXRef.current = true;
      }
    }

    if (nearEdgeXRef.current) {
      if (x < EDGE_START) {
        const d = Math.max(0, EDGE_START - x);
        targetDx = +Math.min(MAX_STEP, d * RATE); // ขอบซ้าย -> panBy ขวา
      } else if (x > rect.width - EDGE_START) {
        const d = Math.max(0, EDGE_START - (rect.width - x));
        targetDx = -Math.min(MAX_STEP, d * RATE); // ขอบขวา -> panBy ซ้าย
      }
    }

    // ===== Y-axis with hysteresis =====
    if (nearEdgeYRef.current) {
      if (y > EDGE_RELEASE && y < rect.height - EDGE_RELEASE) {
        nearEdgeYRef.current = false;
      }
    } else {
      if (y < EDGE_START || y > rect.height - EDGE_START) {
        nearEdgeYRef.current = true;
      }
    }

    if (nearEdgeYRef.current) {
      if (y < EDGE_START) {
        const d = Math.max(0, EDGE_START - y);
        targetDy = -Math.min(MAX_STEP, d * RATE); // ขอบบน -> panBy ขึ้น
      } else if (y > rect.height - EDGE_START) {
        const d = Math.max(0, EDGE_START - (rect.height - y));
        targetDy = +Math.min(MAX_STEP, d * RATE); // ขอบล่าง -> panBy ลง
      }
    }

    // ===== Smoothing (EMA) =====
    const { dx: prevDx, dy: prevDy } = velRef.current;
    const dx = prevDx * (1 - SMOOTH_ALPHA) + targetDx * SMOOTH_ALPHA;
    const dy = prevDy * (1 - SMOOTH_ALPHA) + targetDy * SMOOTH_ALPHA;
    velRef.current = { dx, dy };

    if (Math.abs(dx) > 0.2 || Math.abs(dy) > 0.2) {
      map.panBy(dx, dy);
    }

    // ลูปต่อจนกว่าจะเลิกลาก
    rafRef.current = requestAnimationFrame(autoPanTick);
  }, [map]);

  const startAutoPan = useCallback(() => {
    if (rafRef.current) return;
    rafRef.current = requestAnimationFrame(autoPanTick);
  }, [autoPanTick]);

  const stopAutoPan = useCallback(() => {
    if (rafRef.current) cancelAnimationFrame(rafRef.current);
    rafRef.current = null;
    // reset smoothing
    velRef.current = { dx: 0, dy: 0 };
    // ออกจากโหมดขอบ
    nearEdgeXRef.current = false;
    nearEdgeYRef.current = false;
  }, []);

  /** pointer listeners (ตำแหน่งเมาส์บน container) */
  const attachPointerListeners = useCallback(() => {
    if (!map) return;
    const div = map.getDiv();
    if (div._mp_onMove) return;

    const onMove = (e) => {
      const rect = div.getBoundingClientRect();
      pointerPxRef.current = {
        x: e.clientX - rect.left,
        y: e.clientY - rect.top,
      };
    };
    const onLeave = () => {
      // ป้องกันกระพริบเมื่อหลุดนอก container
      pointerPxRef.current = { x: null, y: null };
    };

    div.addEventListener("pointermove", onMove);
    div.addEventListener("pointerleave", onLeave);
    div._mp_onMove = onMove;
    div._mp_onLeave = onLeave;
  }, [map]);

  const detachPointerListeners = useCallback(() => {
    if (!map) return;
    const div = map.getDiv();
    if (div._mp_onMove) {
      div.removeEventListener("pointermove", div._mp_onMove);
      delete div._mp_onMove;
    }
    if (div._mp_onLeave) {
      div.removeEventListener("pointerleave", div._mp_onLeave);
      delete div._mp_onLeave;
    }
    pointerPxRef.current = { x: null, y: null };
  }, [map]);

  /** Google Map lifecycle */
  const onLoad = useCallback((m) => {
    setMap(m);
    setTimeout(() => setMapReady(true), 300);
  }, []);

  const onUnmount = useCallback(() => {
    stopAutoPan();
    detachPointerListeners();
    setMap(null);
  }, [detachPointerListeners, stopAutoPan]);

  /** interactions */
  const onMapClick = useCallback(
    (event) => {
      if (disabled) return;
      if (event?.latLng) {
        const newPosition = {
          lat: event.latLng.lat(),
          lng: event.latLng.lng(),
        };
        setMarker(newPosition);
        onChange?.(newPosition);
        message.success("วางหมุดแล้ว!", 1);
      }
    },
    [disabled, onChange]
  );

  const getCurrentLocation = () => {
    if (disabled) return;
    setIsLocating(true);
    if (navigator.geolocation) {
      navigator.geolocation.getCurrentPosition(
        (pos) => {
          const newPosition = {
            lat: pos.coords.latitude,
            lng: pos.coords.longitude,
          };
          setMarker(newPosition);
          onChange?.(newPosition);
          if (map) {
            map.panTo(newPosition);
            map.setZoom(16);
          }
          setIsLocating(false);
          message.success("ได้รับตำแหน่งปัจจุบันแล้ว");
        },
        (err) => {
          console.error("Geolocation error:", err);
          setIsLocating(false);
          message.error("ไม่สามารถรับตำแหน่งปัจจุบันได้");
        },
        { enableHighAccuracy: true, timeout: 10000, maximumAge: 0 }
      );
    } else {
      setIsLocating(false);
      message.error("เบราว์เซอร์ไม่รองรับการหาตำแหน่ง");
    }
  };

  const centerOnMarker = () => {
    if (map && marker) {
      map.panTo(marker);
      map.setZoom(16);
    }
  };

  if (loadError) {
    console.error("Google Maps load error:", loadError);
    return (
      <Alert
        message="ไม่สามารถโหลด Google Maps ได้"
        description={
          <div>
            <p>กรุณาตรวจสอบ:</p>
            <ul style={{ margin: 0, paddingLeft: 20 }}>
              <li>การเชื่อมต่ออินเทอร์เน็ต</li>
              <li>Google Maps API Key ในไฟล์ .env</li>
              <li>เปิดใช้งาน Maps JavaScript API</li>
              <li>ตรวจสอบ Domain restrictions ของ API Key</li>
            </ul>
          </div>
        }
        type="error"
        showIcon
        style={{ marginBottom: 16 }}
      />
    );
  }

  if (!process.env.REACT_APP_GOOGLE_MAPS_API_KEY) {
    return (
      <Alert
        message="ไม่พบ Google Maps API Key"
        description={
          <div>
            <p>กรุณาเพิ่ม Google Maps API Key ในไฟล์ .env:</p>
            <code
              style={{
                display: "block",
                background: "#f5f5f5",
                padding: "8px",
                marginTop: "8px",
                borderRadius: "4px",
              }}
            >
              REACT_APP_GOOGLE_MAPS_API_KEY=your_api_key_here
            </code>
          </div>
        }
        type="warning"
        showIcon
        style={{ marginBottom: 16 }}
      />
    );
  }

  if (!isLoaded) {
    return (
      <Card
        style={{
          height: "400px",
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
        }}
      >
        <Spin size="large">
          <div style={{ minHeight: 200, textAlign: "center", paddingTop: 80 }}>
            กำลังโหลดแผนที่...
          </div>
        </Spin>
      </Card>
    );
  }

  return (
    <div>
      <Card
        size="small"
        style={{ marginBottom: 8 }}
        bodyStyle={{ padding: "8px 12px" }}
      >
        <Space split={<span style={{ color: "#d9d9d9" }}>|</span>}>
          <Button
            size="small"
            icon={<AimOutlined />}
            onClick={getCurrentLocation}
            loading={isLocating}
            disabled={disabled}
          >
            ตำแหน่งปัจจุบัน
          </Button>
          <Button
            size="small"
            icon={<EnvironmentOutlined />}
            onClick={centerOnMarker}
            disabled={disabled || !marker}
          >
            ไปที่หมุด
          </Button>
          <Text style={{ fontSize: 12, color: "#666" }}>
            คลิกบนแผนที่เพื่อวางหมุด
          </Text>
        </Space>
      </Card>

      <div style={containerStyle}>
        <GoogleMap
          mapContainerStyle={containerStyle}
          center={marker && marker.lat && marker.lng ? marker : defaultCenter}
          zoom={15}
          onLoad={onLoad}
          onUnmount={onUnmount}
          onClick={onMapClick}
          options={{
            streetViewControl: false,
            mapTypeControl: true,
            fullscreenControl: false,
            zoomControl: true,
            gestureHandling: "greedy",
            panControl: false,
            disableDefaultUI: false,
          }}
        >
          {mapReady &&
            marker &&
            typeof marker.lat === "number" &&
            typeof marker.lng === "number" && (
              <Marker
                key={`marker-${marker.lat}-${marker.lng}`}
                position={{ lat: marker.lat, lng: marker.lng }}
                draggable={!disabled}
                visible={true}
                onDragStart={() => {
                  isDraggingRef.current = true;
                  attachPointerListeners();
                  startAutoPan();
                }}
                onDrag={(e) => {
                  // ถ้าต้องการอัปเดตฟอร์มแบบ real-time:
                  // onChange?.({ lat: e.latLng.lat(), lng: e.latLng.lng() });
                }}
                onDragEnd={(e) => {
                  isDraggingRef.current = false;
                  stopAutoPan();
                  detachPointerListeners();

                  if (disabled) return;
                  const newPosition = {
                    lat: e.latLng.lat(),
                    lng: e.latLng.lng(),
                  };
                  setMarker(newPosition);
                  onChange?.(newPosition);
                  message.success("ย้ายหมุดแล้ว!", 1);
                }}
                title="คลิกและลากเพื่อย้ายตำแหน่ง"
                options={{
                  optimized: false,
                  animation: window.google?.maps?.Animation?.DROP,
                  zIndex: 1000,
                  crossOnDrag: false,
                }}
              />
            )}
        </GoogleMap>
      </div>

      {marker && (
        <Card
          size="small"
          style={{ marginTop: 8 }}
          bodyStyle={{ padding: "8px 12px" }}
        >
          <Space>
            <Text strong style={{ color: "#1890ff" }}>
              พิกัดที่เลือก:
            </Text>
            <Text code style={{ fontSize: 12 }}>
              {marker.lat ? marker.lat.toFixed(6) : "N/A"},{" "}
              {marker.lng ? marker.lng.toFixed(6) : "N/A"}
            </Text>
          </Space>
        </Card>
      )}
    </div>
  );
};

export default MapPicker;
